import React from 'react';

/**
 * JourneyStrip — thin editorial progress wayfinder for the booking journey.
 * Used on BookingSystem section, PaymentSuccess, PaymentCancel.
 *
 * Props:
 *  - steps: [{ id, label }]
 *  - currentStep: id of the active step (highlighted blush)
 *  - tone: 'success' | 'paused' | 'forward' — affects current-step styling
 */
const JourneyStrip = ({ steps, currentStep, tone = 'forward' }) => {
  const activeIdx = steps.findIndex((s) => s.id === currentStep);

  const accent =
    tone === 'success'
      ? 'text-blush border-blush'
      : tone === 'paused'
        ? 'text-platinum border-platinum'
        : 'text-blush border-blush';

  return (
    <div
      data-testid="journey-strip"
      className="flex items-center justify-between gap-3 sm:gap-5 w-full"
    >
      {steps.map((step, idx) => {
        const isCurrent = idx === activeIdx;
        const isPast = activeIdx >= 0 && idx < activeIdx;
        const isFuture = activeIdx >= 0 && idx > activeIdx;

        return (
          <React.Fragment key={step.id}>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span
                className={`flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full border text-[10px] tracking-[0.18em] font-serif transition-colors duration-300 ${
                  isCurrent
                    ? `${accent} bg-onyx`
                    : isPast
                      ? 'border-pewter text-platinum bg-onyx'
                      : isFuture
                        ? 'border-pewter/60 text-silverx/70 bg-transparent'
                        : 'border-pewter text-platinum bg-transparent'
                }`}
              >
                {String(idx + 1).padStart(2, '0').slice(-2)}
              </span>
              <span
                className={`text-[10px] sm:text-[11px] tracking-[0.22em] uppercase whitespace-nowrap transition-colors duration-300 ${
                  isCurrent
                    ? tone === 'paused'
                      ? 'text-platinum'
                      : 'text-blush'
                    : isPast
                      ? 'text-platinum'
                      : 'text-silverx/70'
                }`}
              >
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <span
                aria-hidden="true"
                className={`flex-1 h-px transition-colors duration-300 ${
                  isPast || isCurrent ? 'bg-platinum/40' : 'bg-pewter/60'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default JourneyStrip;
